import { useEffect, useState, type ReactNode } from "react"
import { Zap, type LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { EmptyState } from "../EmptyState/EmptyState"
import { OptimizerConfigRanges } from "../OptimizerConfigRanges/OptimizerConfigRanges"
import { OptimizerMobileDrawer } from "../OptimizerMobileDrawer/OptimizerMobileDrawer"
import { OptimizerRunningBanner } from "../OptimizerRunningBanner/OptimizerRunningBanner"
import { OptimizerTable } from "../OptimizerTable/OptimizerTable"
import { OptimizerToolbar } from "../OptimizerToolbar/OptimizerToolbar"
import type {
  OptimizerParamGroups,
  OptimizerRow,
} from "../types/optimizer"

export type StrategyOptimizerViewState = "loaded" | "running" | "empty"

export interface StrategyOptimizerViewProps {
  state?: StrategyOptimizerViewState
  rows: OptimizerRow[]
  ranges: OptimizerParamGroups
  focusLabel: string
  toolbarInfoSlot?: ReactNode
  deployedRanks?: Set<number>
  showConfig?: boolean
  defaultShowConfig?: boolean
  onShowConfigChange?: (next: boolean) => void
  selectedRow?: OptimizerRow | null
  defaultSelectedRow?: OptimizerRow | null
  onSelectedRowChange?: (row: OptimizerRow | null) => void
  onRunBacktest?: (row: OptimizerRow) => void
  onDeployRow?: (row: OptimizerRow) => void
  drawerTitle?: (row: OptimizerRow) => string
  runBacktestLabel?: string
  deployLabel?: string
  deployedLabel?: string
  mobileBreakpoint?: number
  emptyIcon?: LucideIcon
  emptyTitle?: ReactNode
  emptyBody?: ReactNode
  runningMessage?: ReactNode
  className?: string
}

function useIsBelow(bp: number) {
  const [below, setBelow] = useState(
    typeof window !== "undefined" ? window.innerWidth < bp : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp - 1}px)`)
    setBelow(mq.matches)
    const handler = (e: MediaQueryListEvent) => setBelow(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [bp])
  return below
}

export function StrategyOptimizerView({
  state = "loaded",
  rows,
  ranges,
  focusLabel,
  toolbarInfoSlot,
  deployedRanks,
  showConfig,
  defaultShowConfig = false,
  onShowConfigChange,
  selectedRow,
  defaultSelectedRow = null,
  onSelectedRowChange,
  onRunBacktest,
  onDeployRow,
  drawerTitle,
  runBacktestLabel,
  deployLabel,
  deployedLabel,
  mobileBreakpoint = 768,
  emptyIcon = Zap,
  emptyTitle = "No optimization results yet",
  emptyBody = "Run an optimization from the chat to find the best parameter combinations for your strategy.",
  runningMessage,
  className,
}: StrategyOptimizerViewProps) {
  const [internalShowConfig, setInternalShowConfig] = useState(defaultShowConfig)
  const isShowConfigControlled = showConfig !== undefined
  const currentShowConfig = isShowConfigControlled ? showConfig : internalShowConfig

  const [internalSelectedRow, setInternalSelectedRow] = useState<OptimizerRow | null>(
    defaultSelectedRow,
  )
  const isSelectedControlled = selectedRow !== undefined
  const currentSelectedRow = isSelectedControlled ? selectedRow : internalSelectedRow

  const isMobile = useIsBelow(mobileBreakpoint)

  const handleToggleConfig = () => {
    const next = !currentShowConfig
    if (!isShowConfigControlled) setInternalShowConfig(next)
    onShowConfigChange?.(next)
  }

  const setSelected = (row: OptimizerRow | null) => {
    if (!isSelectedControlled) setInternalSelectedRow(row)
    onSelectedRowChange?.(row)
  }

  const handleRowSelect = (row: OptimizerRow) => {
    if (isMobile) setSelected(row)
  }

  const handleDrawerOpenChange = (next: boolean) => {
    if (!next) setSelected(null)
  }

  const isRunning = state === "running"
  const isEmpty = state === "empty"

  if (isEmpty) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        body={emptyBody}
        className={cn("bg-card", className)}
      />
    )
  }

  return (
    <div className={cn("h-full overflow-y-auto bg-card", className)}>
      <div className="mx-auto flex w-full max-w-[90rem] flex-col gap-4 p-4">
        {isRunning && <OptimizerRunningBanner message={runningMessage} />}

        <OptimizerToolbar
          focusLabel={focusLabel}
          infoSlot={toolbarInfoSlot}
          showConfig={currentShowConfig}
          onToggle={handleToggleConfig}
        />

        {currentShowConfig && <OptimizerConfigRanges ranges={ranges} />}

        <OptimizerTable
          rows={rows}
          deployedRanks={deployedRanks}
          onRunBacktest={onRunBacktest}
          onDeployRow={onDeployRow}
          onRowSelect={handleRowSelect}
        />
      </div>

      <OptimizerMobileDrawer
        open={currentSelectedRow !== null}
        onOpenChange={handleDrawerOpenChange}
        row={currentSelectedRow}
        title={
          currentSelectedRow && drawerTitle ? drawerTitle(currentSelectedRow) : undefined
        }
        isDeployed={
          currentSelectedRow
            ? (deployedRanks?.has(currentSelectedRow.rank) ?? false)
            : false
        }
        runBacktestLabel={runBacktestLabel}
        deployLabel={deployLabel}
        deployedLabel={deployedLabel}
        onRunBacktest={
          onRunBacktest
            ? (row) => {
                onRunBacktest(row)
                setSelected(null)
              }
            : undefined
        }
        onDeploy={
          onDeployRow
            ? (row) => {
                onDeployRow(row)
                setSelected(null)
              }
            : undefined
        }
      />
    </div>
  )
}
